export type DocumentAccessAction = {
  label: string;
  url: string;
};

export type DocumentAccessNotice = {
  code?: string;
  title: string;
  message: string;
  secondaryMessage?: string;
  primaryAction?: DocumentAccessAction;
};

export class ProtectedDocumentError extends Error {
  notice: DocumentAccessNotice;

  constructor(notice: DocumentAccessNotice) {
    super(notice.message);
    this.name = 'ProtectedDocumentError';
    this.notice = notice;
  }
}

export function getIntroCallUrl(source = 'offering-docs') {
  return `/portal/onboarding-call?source=${encodeURIComponent(source)}`;
}

export function getDefaultDocumentAccessNotice(): DocumentAccessNotice {
  return {
    code: 'OFFERING_ACCESS_PENDING',
    title: 'Offering Access Pending',
    message: 'Your investor profile is currently under review. Offering documents become available after your access is approved. Schedule your onboarding call to complete the next step.',
    secondaryMessage: 'Already completed your call? We will notify you when your access has been approved.',
    primaryAction: { label: 'Schedule Intro Call', url: getIntroCallUrl('offering-docs') },
  };
}

export function getProtectedDocumentNotice(error: unknown): DocumentAccessNotice {
  if (error instanceof ProtectedDocumentError) return error.notice;
  return getDefaultDocumentAccessNotice();
}

function filenameFromResponse(response: Response, fallback: string) {
  const disposition = response.headers.get('content-disposition') || '';
  const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  if (!filenameMatch) return fallback;
  try {
    return decodeURIComponent(filenameMatch[1]);
  } catch (_) {
    return filenameMatch[1];
  }
}

export async function openProtectedDocument(url: string, fallbackFilename = 'document') {
  const token = localStorage.getItem('authToken');
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const notice: DocumentAccessNotice = data?.title || data?.message
      ? {
          code: data.code,
          title: data.title || 'Offering Access Pending',
          message: data.message || getDefaultDocumentAccessNotice().message,
          secondaryMessage: data.secondaryMessage,
          primaryAction: data.primaryAction || getDefaultDocumentAccessNotice().primaryAction,
        }
      : getDefaultDocumentAccessNotice();
    throw new ProtectedDocumentError(notice);
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const filename = filenameFromResponse(response, fallbackFilename);

  const link = document.createElement('a');
  link.href = objectUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 30_000);
}